import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { translate, useLanguage } from './i18n/index.js';
import { useApp } from './store.js';
import { DisconnectedState, type Mode } from './states/DisconnectedState.js';
import { browserEntry, showSiteFirst } from './landing/entry.js';
import { legalRoute } from './legal/documents.js';
import { LoggedState } from './states/LoggedState.js';
import { ConfirmEmailState } from './states/ConfirmEmailState.js';
import { takeEmailLink } from './states/emailLink.js';
import { TermsUpdateDialog } from './states/TermsUpdateDialog.js';
import { PromptHost } from './ui/prompt.js';
import { WebInstallDialog } from './plugins/web/WebInstallDialog.js';
import { Toaster, toast } from './ui/Toaster.js';
import { POPOUT } from './windowMode.js';
import { PopoutApp } from './room/PopoutApp.js';

// o site para quem chega sem conta (docs/spec/landing.md): só quem o vê baixa a página e as imagens
const LandingPage = lazy(() => import('./landing/LandingPage.js').then((module) => ({ default: module.LandingPage })));

// as páginas públicas dos termos e da privacidade (docs/spec/termos.md): o endereço escolhe o texto e
// o idioma, que passa a valer antes do primeiro desenho
const LegalPage = lazy(() => import('./legal/LegalPage.js').then((module) => ({ default: module.LegalPage })));
const LEGAL = typeof window === 'undefined' ? null : legalRoute(window.location.pathname);
if (LEGAL && useLanguage.getState().language !== LEGAL.language) useLanguage.getState().setLanguage(LEGAL.language);

/**
 * O link do e-mail da confirmação (docs/spec/email.md). O código fica no endereço até a entrada decidir
 * o que mostrar (o formulário de entrar, e não o site), e o confirmar acontece uma vez só, antes do
 * /api/me, que assim já vem sem a espera.
 */
let emailLinkHandled: Promise<void> | null = null;
function handleEmailLink(): Promise<void> {
  emailLinkHandled ??= (async () => {
    const code = takeEmailLink();
    if (code) await useApp.getState().confirmEmailLink(code);
  })();
  return emailLinkHandled;
}

export function App() {
  // uma aba da mesa numa janela própria (docs/spec/popout-windows.md)
  if (POPOUT) return <PopoutApp spec={POPOUT} />;
  if (LEGAL)
    return (
      <Suspense fallback={<div className="rd-disconnected" />}>
        <LegalPage initial={LEGAL.doc} />
      </Suspense>
    );
  return <MainApp />;
}

function MainApp() {
  const initialize = useApp((state) => state.initialize);
  const user = useApp((state) => state.user);
  const error = useApp((state) => state.error);
  const dismissError = useApp((state) => state.dismissError);
  const removedNotice = useApp((state) => state.removedNotice);
  const tosPending = useApp((state) => state.tosPending);
  const dismissRemovedNotice = useApp((state) => state.dismissRemovedNotice);
  const emailConfirmation = useApp((state) => state.emailConfirmation);
  const emailLinkNotice = useApp((state) => state.emailLinkNotice);
  const dismissEmailLinkNotice = useApp((state) => state.dismissEmailLinkNotice);
  const entryView = useEntryView(!!user);

  useEffect(() => {
    void handleEmailLink().then(() => initialize());
  }, [initialize]);

  // com a conta aberta, o resultado do link do e-mail vira aviso rápido; sem ela, a tela de entrada mostra
  useEffect(() => {
    if (!emailLinkNotice || !user) return;
    toast(translate(useLanguage.getState().language, `confirmEmail.toast.${emailLinkNotice}`), emailLinkNotice === 'confirmed' ? 'success' : 'error');
    dismissEmailLinkNotice();
  }, [emailLinkNotice, user, dismissEmailLinkNotice]);

  // erros do servidor e da API viram avisos rápidos (no login, a própria tela mostra no campo)
  useEffect(() => {
    if (!error || !user) return;
    toast(error, 'error');
    dismissError();
  }, [error, user, dismissError]);

  useEffect(() => {
    if (!removedNotice) return;
    const language = useLanguage.getState().language;
    toast(translate(language, `room.removed.${removedNotice.reason}`, { by: removedNotice.byName }), 'error');
    dismissRemovedNotice();
  }, [removedNotice, dismissRemovedNotice]);

  return (
    <div className="app">
      {user ? (
        // a conta que espera a confirmação do e-mail fica na tela de espera (docs/spec/email.md)
        emailConfirmation ? (
          <ConfirmEmailState />
        ) : (
          <LoggedState />
        )
      ) : entryView.view === 'site' ? (
        <Suspense fallback={<div className="rd-disconnected" />}>
          <LandingPage onSignIn={() => entryView.openForm('login')} onSignUp={() => entryView.openForm('step1')} />
        </Suspense>
      ) : (
        <DisconnectedState key={entryView.view} initialMode={entryView.view} onSite={entryView.siteAllowed ? entryView.openSite : undefined} />
      )}
      {/* os termos mudaram desde o último aceite: a conta espera o novo aceite (docs/spec/termos.md) */}
      {user && tosPending && !emailConfirmation ? <TermsUpdateDialog /> : null}
      {/* antes das perguntas: a confirmação de atualizar um oficial, que sai do catálogo, abre por cima dele */}
      <WebInstallDialog />
      <PromptHost />
      <Toaster />
    </div>
  );
}

/**
 * O que quem não está logado vê (docs/spec/landing.md): o site ou o formulário (entrar ou cadastro).
 * Abrir o formulário pelo site entra no histórico do navegador, sem mudar o endereço, e o Voltar
 * volta ao site. Quem entra e depois sai da conta volta ao formulário de entrar.
 */
function useEntryView(loggedIn: boolean) {
  const [siteAllowed] = useState(() => {
    const entry = browserEntry(null);
    return !entry.desktop && !entry.standalone;
  });
  const [view, setView] = useState<'site' | Mode>(() => (showSiteFirst(browserEntry(useApp.getState().token)) ? 'site' : 'login'));

  // entrou na conta: ao sair, volta ao formulário de entrar
  const [wasLoggedIn, setWasLoggedIn] = useState(loggedIn);
  if (loggedIn !== wasLoggedIn) {
    setWasLoggedIn(loggedIn);
    if (loggedIn) setView('login');
  }

  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      if (useApp.getState().user) return;
      const mode = (event.state as { rdEntry?: Mode } | null)?.rdEntry;
      setView(mode ?? (siteAllowed ? 'site' : 'login'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [siteAllowed]);

  const openForm = useCallback((mode: Mode) => {
    setView(mode);
    window.history.pushState({ rdEntry: mode }, '');
  }, []);
  // o formulário aberto pelo site volta pelo histórico (o Avançar reabre); aberto direto, só troca
  const openSite = useCallback(() => {
    if ((window.history.state as { rdEntry?: Mode } | null)?.rdEntry) window.history.back();
    else setView('site');
  }, []);

  return { view, siteAllowed, openForm, openSite };
}
